<script setup>
/**
 * The team, and which book needs help.
 *
 * Two jobs, in this order: spot a book in trouble, and be the way into one
 * broker's world. The second is why this lives on the Overview at all — a row
 * click sets the page's broker filter rather than navigating, so the whole page
 * becomes that broker's Overview in place.
 *
 * Headed "Your brokers", above the card rather than inside it, as the design
 * board draws every section on this page.
 *
 * It is deliberately not a league table. Two things follow: the default order
 * is by work arriving (Waiting, then Median reply) rather than by book size —
 * which would read as a ranking — and Median reply is shown, never used to
 * drive any ordering of its own. "Waiting" and "median reply" used to be
 * unbuildable from this application; see TeamReplyStats on the backend for
 * where they come from now.
 */
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useBrokerScopeStore } from '../../../stores/brokerScope';
import XlAvatar from '../../xl/XlAvatar.vue';
import XlCard from '../../xl/XlCard.vue';
import XlTable from '../../xl/XlTable.vue';
import XlTag from '../../xl/XlTag.vue';
import XlTooltip from '../../xl/XlTooltip.vue';
import { TERM_TIPS } from '../../../helpers/termTips';

const props = defineProps({
  team: { type: Object, default: null },
});

const router = useRouter();
const route = useRoute();
const brokerScope = useBrokerScopeStore();

const days = computed(() => props.team?.renewing_days ?? 30);

// Provisional, pending agency sign-off — the design brief suggests these
// exact bands while flagging them as unconfirmed. Change here if the agency's
// numbers differ; nothing else needs to know about it.
const REPLY_THRESHOLD_MINUTES = { good: 4 * 60, ok: 8 * 60 };

const replyTone = (minutes) => {
  if (minutes === null || minutes === undefined) return null;
  if (minutes < REPLY_THRESHOLD_MINUTES.good) return 'success';
  if (minutes < REPLY_THRESHOLD_MINUTES.ok) return 'warning';

  return 'error';
};

const formatReplyMinutes = (minutes) => `${(minutes / 60).toFixed(1)}h`;

/**
 * Whoever has the most work arriving sits at the top. That is a workload
 * signal, not a judgement of the person — which is why the default is never
 * book size, however tempting the ordering looks.
 */
const sortKey = ref('waiting');
const sortDescending = ref(true);

const COLUMNS = [
  { key: 'policyholders', label: 'Policyholders' },
  { key: 'renewing_soon', label: `Renewing ≤${30}d` },
  { key: 'waiting', label: 'Waiting' },
  { key: 'median_reply_minutes', label: 'Median reply' },
];

const columns = computed(() =>
  COLUMNS.map((column) => (
    column.key === 'renewing_soon' ? { ...column, label: `Renewing ≤${days.value}d` } : column
  ))
);

const rows = computed(() => {
  const list = [...(props.team?.brokers ?? [])];

  return list.sort((a, b) => {
    // A withheld number sorts last rather than as a nought: not knowing is not
    // the same as nothing to do.
    const left = a[sortKey.value] ?? -1;
    const right = b[sortKey.value] ?? -1;

    if (left !== right) return sortDescending.value ? right - left : left - right;

    // The default sort's own tie-break, per the design brief: Waiting first,
    // Median reply second. Any other column click keeps the plain name
    // tie-break below — this one extra step is specific to the default.
    if (sortKey.value === 'waiting') {
      const leftReply = a.median_reply_minutes ?? -1;
      const rightReply = b.median_reply_minutes ?? -1;

      if (leftReply !== rightReply) return rightReply - leftReply;
    }

    return (a.name ?? '').localeCompare(b.name ?? '');
  });
});

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortDescending.value = !sortDescending.value;

    return;
  }

  sortKey.value = key;
  sortDescending.value = true;
};

const isSelected = (broker) => brokerScope.brokerId === String(broker.id);

/**
 * The page becomes that broker's Overview without navigating — Overview.vue
 * watches this and refetches. Clicking the selected row again clears it.
 */
const toggleFilter = (broker) => {
  brokerScope.set(isSelected(broker) ? '' : String(broker.id), router, route);
};

const openList = (path, query) => router.push({ path, query });

const openColumn = (broker, key) => {
  // No destination page exists yet for a broker's response-time detail — see
  // OverviewTeam's docblock and the plan this shipped under. The metric is
  // real; this cell just isn't a link yet.
  if (key === 'median_reply_minutes') return;

  // Zeros are inert here. This differs from the counts row, where a zero card
  // stays clickable so an empty list can prove the number — in a table most
  // cells are zero, and making them all live targets is noise rather than
  // honesty.
  if (!broker[key]) return;

  if (key === 'waiting') {
    openList('/messages', { broker_id: String(broker.id) });

    return;
  }

  const base = { tab: 'registered', broker_id: String(broker.id) };

  if (key === 'renewing_soon') openList('/policyholders', { ...base, renewal_within: String(days.value) });
  else openList('/policyholders', base);
};

const cell = (broker, key) => (broker[key] === null || broker[key] === undefined ? '—' : broker[key]);
</script>

<template>
  <section v-if="props.team" class="team">
    <!-- Above the card, like every other section heading on this page. -->
    <div class="team__head">
      <h2 class="team__title xl-section-heading">Your brokers</h2>
    </div>

    <XlCard padded>
      <XlTable>
        <template #head>
          <th><XlTooltip :text="TERM_TIPS.Broker" placement="bottom" underline>Broker</XlTooltip></th>
          <th
            v-for="column in columns"
            :key="column.key"
            class="team__sortable"
            :aria-sort="sortKey === column.key ? (sortDescending ? 'descending' : 'ascending') : 'none'"
            :data-testid="`team-sort-${column.key}`"
            tabindex="0"
            @click="sortBy(column.key)"
            @keydown.enter.prevent="sortBy(column.key)"
            @keydown.space.prevent="sortBy(column.key)"
          >
            {{ column.label }}
          </th>
        </template>

        <tr
          v-for="broker in rows"
          :key="broker.id"
          class="is-clickable"
          :class="{ 'is-selected': isSelected(broker) }"
          :data-testid="`team-row-${broker.id}`"
          role="button"
          tabindex="0"
          @click="toggleFilter(broker)"
          @keydown.enter="toggleFilter(broker)"
          @keydown.space.prevent="toggleFilter(broker)"
        >
          <td>
            <span class="team__broker">
              <XlAvatar :name="broker.name ?? ''" :src="broker.photo || ''" :size="30" />
              <span class="team__name">{{ broker.name ?? 'Unnamed broker' }}</span>
              <XlTag v-if="broker.on_duty" tone="success" data-testid="team-on-duty">on duty</XlTag>
            </span>
          </td>

          <td
            v-for="column in columns"
            :key="column.key"
            :class="{ 'team__number--live': column.key !== 'median_reply_minutes' && broker[column.key] }"
            :data-testid="`team-${column.key}-${broker.id}`"
            @click.stop="openColumn(broker, column.key)"
          >
            <XlTag
              v-if="column.key === 'median_reply_minutes' && broker[column.key] !== null && broker[column.key] !== undefined"
              :tone="replyTone(broker[column.key])"
            >
              {{ formatReplyMinutes(broker[column.key]) }}
            </XlTag>
            <template v-else>{{ cell(broker, column.key) }}</template>
          </td>
        </tr>
      </XlTable>
    </XlCard>
  </section>
</template>

<style scoped>
.team {
  margin-top: 20px;
}

.team__head {
  display: flex;
  align-items: baseline;
  margin-bottom: 10px;
}

.team__title {
  display: flex;
  flex: 1;
  align-items: baseline;
  gap: 12px;
}

.team__title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--xl-color-border);
}

.team__sortable {
  cursor: pointer;
  user-select: none;
}

.team__broker {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.team > :deep(.xl-card) {
  padding: 0;
}

.team :deep(th) {
  height: 46px;
  padding: 10px 16px;
}

.team :deep(td) {
  padding: 13px 16px;
  border-bottom: 1px solid var(--xl-color-border-subtle);
  font-size: 14px;
  font-weight: 400;
}

.team :deep(tr:last-child td) {
  border-bottom: 0;
}

.team :deep(tbody td:first-child) {
  width: 24%;
  max-width: 0;
}

.team__broker :deep(.xl-tag) {
  flex-shrink: 0;
}

.team__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
}

/* Only a number with something behind it looks like a target. */
.team__number--live {
  cursor: pointer;
  font-weight: var(--xl-font-weight-bold);
}

.team__number--live:hover {
  text-decoration: underline;
}
</style>
